import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../seo";
import { api, articleImage } from "../api";
import { Reveal, Breadcrumbs } from "../components/motion";

export default function ArticleDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null); setNotFound(false);
        api.get(`/articles/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return (
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
            <h1 className="font-display text-3xl text-navy">Article not found</h1>
            <Link to="/patient-education" data-testid="back-to-articles-btn" className="mt-6 inline-block text-accent font-semibold">Back to Patient Education</Link>
        </div>
    );
    if (!data) return <div className="mx-auto max-w-7xl px-5 py-24 text-steel">Loading…</div>;

    const a = data.article;
    return (
        <article className="mx-auto max-w-3xl px-5 py-14 lg:py-20" data-testid="article-detail">
            <Seo title={a.seo_title || `${a.title} | Dr. Krishnakanth AVB`} description={a.meta_description || a.summary}
                jsonLd={{
                    "@context": "https://schema.org", "@type": "Article",
                    "headline": a.title, "description": a.summary,
                    "image": articleImage(a),
                    "datePublished": a.date,
                    "author": { "@type": "Physician", "name": a.author },
                }} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Patient Education", to: "/patient-education" }, { label: a.title }]} />
            <Reveal>
                <p className="mt-10 font-mono-label text-accent">{a.category}</p>
                <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">{a.title}</h1>
                <p className="mt-6 text-sm text-steel">
                    By <span className="font-semibold text-navy">{a.author}</span> · {a.date} · {a.reading_time} min read
                </p>
            </Reveal>
            <Reveal delay={0.1}>
                <div className="mt-10 overflow-hidden rounded-2xl bg-mist">
                    <img src={articleImage(a)} alt="" className="h-64 w-full object-cover sm:h-80" data-testid="article-featured-image" />
                </div>
            </Reveal>
            <div className="mt-12 space-y-7" data-testid="article-content">
                {(a.content || []).map((para, i) => (
                    <Reveal key={i}>
                        <p className="text-base leading-[1.85] text-slate-700 md:text-lg">{para}</p>
                    </Reveal>
                ))}
            </div>
            {(a.references || []).length > 0 && (
                <Reveal className="mt-14 rounded-2xl border border-border bg-mist p-7">
                    <h2 className="font-mono-label text-steel">References & Further Reading</h2>
                    <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-steel">
                        {a.references.map((r, i) => <li key={i}>{r}</li>)}
                    </ol>
                </Reveal>
            )}
            <p className="mt-10 border-t border-border pt-6 text-xs leading-relaxed text-slate-400">{data.disclaimer}</p>
            {data.related.length > 0 && (
                <div className="mt-14">
                    <h2 className="font-display text-2xl font-medium text-navy">Related articles</h2>
                    <div className="mt-6 space-y-1 divide-y divide-border border-y border-border">
                        {data.related.map((r) => (
                            <Link key={r.slug} to={`/patient-education/${r.slug}`} data-testid={`related-${r.slug}`}
                                className="group flex items-center justify-between gap-4 py-4">
                                <span className="font-medium text-navy group-hover:text-accent transition-colors">{r.title}</span>
                                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-accent" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </article>
    );
}
